import { useQuery } from '@tanstack/react-query';
import {
  fetchAccountingSummaryKpis,
  fetchAccountingFinancialsSnapshot,
  fetchAccountingRecentJournals,
  fetchAccountingGuestOrigins,
} from '@/renderer/services/accountingReportingService';

export function useAccountingSummaryKpis(
  companyId: number | null,
  outletId: string | null | undefined,
  dateFrom: string,
  dateTo: string,
) {
  return useQuery({
    queryKey: [
      'accounting',
      'dashboard',
      'summaryKpis',
      companyId,
      outletId,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      const result = await fetchAccountingSummaryKpis(
        companyId!,
        outletId,
        dateFrom,
        dateTo,
      );
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to load summary KPIs');
      }
      return result.data;
    },
    enabled: !!companyId && !!dateFrom && !!dateTo,
    staleTime: 1000 * 60 * 5,
  });
}

export function useAccountingFinancials(
  companyId: number | null,
  outletId: string | null | undefined,
  dateFrom: string,
  dateTo: string,
) {
  return useQuery({
    queryKey: [
      'accounting',
      'dashboard',
      'financials',
      companyId,
      outletId,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      const result = await fetchAccountingFinancialsSnapshot(
        companyId!,
        outletId,
        dateFrom,
        dateTo,
      );
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to load financials');
      }
      return result.data;
    },
    enabled: !!companyId && !!dateFrom && !!dateTo,
    staleTime: 1000 * 60 * 5,
  });
}

export function useAccountingRecentJournals(
  companyId: number | null,
  outletId: string | null | undefined,
  dateFrom: string,
  dateTo: string,
) {
  return useQuery({
    queryKey: [
      'accounting',
      'dashboard',
      'recentJournals',
      companyId,
      outletId,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      const result = await fetchAccountingRecentJournals(
        companyId!,
        outletId,
        dateFrom,
        dateTo,
      );
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to load recent journals');
      }
      return result.data;
    },
    enabled: !!companyId && !!dateFrom && !!dateTo,
    staleTime: 1000 * 60 * 5,
  });
}

export function useAccountingGuestOrigins(
  companyId: number | null,
  outletId: string | null | undefined,
  dateFrom: string,
  dateTo: string,
) {
  return useQuery({
    queryKey: [
      'accounting',
      'dashboard',
      'guestOrigins',
      companyId,
      outletId,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      const result = await fetchAccountingGuestOrigins(
        companyId!,
        outletId,
        dateFrom,
        dateTo,
      );
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to load guest origins');
      }
      return result.data;
    },
    enabled: !!companyId && !!dateFrom && !!dateTo,
    staleTime: 1000 * 60 * 5,
  });
}
